
import { Component, effect, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, signal, SimpleChanges, ViewChild } from '@angular/core';
import { MapsService } from '../../../../../core/service/google/maps/maps.service';
import { Subscription } from 'rxjs';
import { MapNewTrackingService } from './service/map-new-tracking.service';

declare const google: any;

@Component({
  selector: 'detail-order-map-tracking',
  standalone: true,
  imports: [],
  templateUrl: './map-tracking.component.html',
  styleUrl: './map-tracking.component.css'
})
export class MapTrackingComponent implements OnInit, OnDestroy {
  @ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef;

  @Input() orderId!: number;
  @Input() timePreparingOrder!: number;

  @Input() coordenadasOrigen!: { lat: number, lng: number };
  @Input() coordenadasDestino!: { lat: number, lng: number };

  private subscriptions: Subscription = new Subscription();
  private originMarker: any;
  private destinationMarker: any;
  private map: any;
  private directionsService: any;

  private directionsRenderer: any;
  private deliveryMarker: any;
  public duration: string = '';
  public adjustedDuration: string = '';
  public distance: string = '';

  public trafficDensity: number = 0;
  public totalTime: string = '';

  currentStatus: number = 40;
  isLoading: boolean = true;
  mapInitialized = false;
  status = signal(3);
  statusAnterior = signal(3);

  @Output() statusChange = new EventEmitter<number>();
  constructor(
    private mapsService: MapsService,

    private mapNewTrackingService: MapNewTrackingService

  ) {
    effect(() => {
      // Reaccionar a cambios en el status del servicio
      const newStatus = this.mapNewTrackingService.status();
      if (newStatus !== 0 && this.changeStatus(newStatus, this.statusAnterior())) {
        // console.log('Emitting status change:', newStatus);
        this.status.set(newStatus);
        this.statusChange.emit(newStatus);
      }
    });

    effect(() => {
      // Reaccionar a cambios en la ubicación del repartidor
      const deliveryLocation = this.mapNewTrackingService.deliveryLocation();
      if (deliveryLocation && this.deliveryMarker && this.mapInitialized) {
        this.updateDeliveryMarkerPosition(deliveryLocation);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.map) {
      google.maps.event.clearInstanceListeners(this.map);
    }
    if (this.originMarker) {
      this.originMarker.setMap(null);
    }
    if (this.destinationMarker) {
      this.destinationMarker.setMap(null);
    }
    if (this.deliveryMarker) {
      this.deliveryMarker.setMap(null);
    }
    if (this.directionsRenderer) {
      this.directionsRenderer.setMap(null);
    }
    this.subscriptions.unsubscribe();
  }

  async ngOnInit() {
    try {
      await this.mapsService.loadGoogleMapsScript();
      await this.initMap();

      // Join room after component initialization when orderId is available
      if (this.orderId) {
        // console.log('Joining room with orderId:', this.orderId);
        this.mapNewTrackingService.joinRoom(Number(this.orderId));
        // Initialize tracking listener
        this.mapNewTrackingService.listenTracking(Number(this.orderId));
      } else {
        // console.log('no hay orden id en ngOnInit');
      }
    } catch (error) {
      console.error('Error initializing map:', error);
      this.isLoading = false;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.mapInitialized && this.areCoordinatesValid()) {
      // this.calculateRoute();
    }

    // Handle orderId changes
    if (changes['orderId'] && changes['orderId'].currentValue) {
      // console.log('OrderId changed to:', changes['orderId'].currentValue);
      this.mapNewTrackingService.joinRoom(Number(changes['orderId'].currentValue));
    }
  }

  private async initMap() {
    try {
      await this.mapsService.loadGoogleMapsScript();

      const initialCenter = {
        lat: this.coordenadasOrigen?.lat || 19.4326,  // Default to Mexico City if coords not available
        lng: this.coordenadasOrigen?.lng || -99.1332
      };

      this.map = new google.maps.Map(this.mapContainer.nativeElement, {
        center: initialCenter,
        zoom: 12,
        disableDefaultUI: true,
        mapTypeId: 'roadmap',
        styles: [
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }]
          },
          {
            featureType: "transit",
            elementType: "labels",
            stylers: [{ visibility: "off" }]
          },
          {
            featureType: "business",
            elementType: "labels",
            stylers: [{ visibility: "off" }]
          }
        ]
      });

      this.directionsService = new google.maps.DirectionsService();
      this.directionsRenderer = new google.maps.DirectionsRenderer({
        map: this.map,
        suppressMarkers: true,  // Changed from false
        hideRouteList: true,
        draggable: false,
        polylineOptions: {
          strokeColor: '#FF6B6B',
          strokeWeight: 5
        },

      });
      const deliveryIcon = {
        url: 'assets/map-tracking/Repartidor.png',
        scaledSize: new google.maps.Size(50, 50),
        anchor: new google.maps.Point(25, 25)
      };
      const houseIcon = {
        url: 'assets/map-tracking/casa.png',
        scaledSize: new google.maps.Size(45, 45),
        anchor: new google.maps.Point(16, 32)
      };

      const storeIcon = {
        url: 'assets/map-tracking/tienda.png',
        scaledSize: new google.maps.Size(50, 50),
        anchor: new google.maps.Point(16, 32)
      };

      this.originMarker = new google.maps.Marker({
        map: this.map,
        icon: houseIcon
      });

      this.destinationMarker = new google.maps.Marker({
        map: this.map,
        icon: storeIcon
      });

      // Crear el marcador del repartidor con logs de depuración
      // console.log('Creating delivery marker with icon:', deliveryIcon);
      this.deliveryMarker = new google.maps.Marker({
        map: this.map,
        icon: deliveryIcon,
        visible: false,
        title: 'Repartidor',
        zIndex: 1000
      });
      // console.log('Delivery marker created:', this.deliveryMarker);

      // Verificar que el marcador se creó correctamente
      if (this.deliveryMarker) {
        // console.log('Delivery marker map:', this.deliveryMarker.getMap());
        // console.log('Delivery marker visible:', this.deliveryMarker.getVisible());
      }
      this.mapInitialized = true;
      this.setupLocationSocket();
      if (this.areCoordinatesValid()) {
        this.calculateRoute();
      }

      // Asegúrate de que el mapa se renderice correctamente
      google.maps.event.addListenerOnce(this.map, 'idle', () => {
        google.maps.event.trigger(this.map, 'resize');
      });

    } catch (error) {
      console.error('Error initializing map:', error);
    }
  }

  private async calculateRoute() {
    if (!this.directionsService || !this.directionsRenderer) return;

    const routeOrigin = { lat: this.coordenadasDestino.lat, lng: this.coordenadasDestino.lng };
    const routeDestination = { lat: this.coordenadasOrigen.lat, lng: this.coordenadasOrigen.lng };

    // Configurar marcadores
    this.originMarker.setPosition(routeOrigin); // Origen (punto de partida)
    this.destinationMarker.setPosition(routeDestination); // Destino (punto final)

    const request = {
      origin: routeDestination, // Desde el origen
      destination: routeOrigin, // Hacia el destino
      travelMode: google.maps.TravelMode.TWO_WHEELER,
      drivingOptions: {
        departureTime: new Date(),
        trafficModel: google.maps.TrafficModel.BEST_GUESS
      }
    };

    this.directionsService.route(request, (result: any, status: any) => {
      if (status === google.maps.DirectionsStatus.OK && result) {
        this.directionsRenderer.setDirections(result);
        const route = result.routes[0];

        if (route && route.legs[0]) {
          this.duration = route.legs[0].duration_in_traffic?.text || route.legs[0].duration?.text || '';
          this.distance = route.legs[0].distance?.text || '';

          this.analyzeTrafficConditions(route);
          this.adjustEstimatedTime();
        }
      } else {
        console.error('Error calculating route:', status);
      }
    });
  }

  private setupLocationSocket() {

  }

  private updateDeliveryMarkerPosition(location: { lat: number, lng: number }) {
    // console.log('updateDeliveryMarkerPosition called with:', location);
    // console.log('deliveryMarker exists:', !!this.deliveryMarker);
    // console.log('map exists:', !!this.map);

    if (this.deliveryMarker && this.map) {


      // Actualizar la posición del marcador
      const newPosition = new google.maps.LatLng(location.lat, location.lng);
      this.deliveryMarker.setPosition(newPosition);

      // Hacer visible el marcador
      this.deliveryMarker.setVisible(true);



      // Asegurar que el marcador esté en el mapa
      if (this.deliveryMarker.getMap() !== this.map) {

        this.deliveryMarker.setMap(this.map);
      }

      // Opcional: centrar el mapa en la nueva posición del repartidor
      // this.map.panTo(newPosition);
    } else {

      if (!this.deliveryMarker) console.error('deliveryMarker is null/undefined');
      if (!this.map) console.error('map is null/undefined');
    }
  }

  private analyzeTrafficConditions(route: any) {
    let totalDensity = 0;
    let segments = 0;

    route.legs[0].steps.forEach((step: any) => {
      if (step.duration_in_traffic && step.duration) {
        const ratio = step.duration_in_traffic.value / step.duration.value;
        totalDensity += ratio;
        segments++;
      }
    });

    this.trafficDensity = segments > 0 ? totalDensity / segments : 1;
  }

  private adjustEstimatedTime() {
    let baseMinutes = this.parseMinutes(this.duration);
    let adjustmentFactor = 1;
    adjustmentFactor *= this.trafficDensity;

    const adjustedMinutes = Math.round(baseMinutes * adjustmentFactor);
    const totalMinutes = adjustedMinutes + this.timePreparingOrder + 15;

    const now = new Date();
    const estimatedArrival = new Date(now.getTime() + totalMinutes * 60000);
    const formattedTime = estimatedArrival.toLocaleTimeString('es-MX', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    this.adjustedDuration = ` ${adjustedMinutes} min`;
    // console.log('adjustedDuration:', this.adjustedDuration );

  }

  private parseMinutes(duration: string): number {
    const matches = duration.match(/(\d+)\s*hr?|(\d+)\s*min/g);
    let minutes = 0;

    if (matches) {
      matches.forEach(match => {
        if (match.includes('hr')) {
          minutes += parseInt(match) * 60;
        } else {
          minutes += parseInt(match);
        }
      });
    }

    return minutes;
  }

  private formatDuration(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours > 0) {
      return `${hours} hr ${remainingMinutes} min`;
    }
    return `${remainingMinutes} min`;
  }

  getTrafficDescription(): string {
    if (this.trafficDensity <= 1.1) return 'Fluido';
    if (this.trafficDensity <= 1.3) return 'Moderado';
    if (this.trafficDensity <= 1.6) return 'Denso';
    return 'Muy denso';
  }

  private areCoordinatesValid(): boolean {
    return !!(this.coordenadasOrigen?.lat && this.coordenadasOrigen?.lng && this.coordenadasDestino?.lat && this.coordenadasDestino?.lng);
  }

  changeStatus(status: number, statusAnterior: number) {
    if (status != statusAnterior) {
      this.statusAnterior.set(status); // Actualizar el status anterior con el nuevo status
      return true;
    }
    return false;
  }
}
